import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import Empty from '../../components/empty/empty';
import ErrorComponent from '../../components/error/error';
import FeatureTitle from '../../components/feature-title/feature-title';
import List from '../../components/list/list';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';

// Helpers
import {
  getDiaryMode,
  getGender,
  getPeriodName,
} from '../../helpers/string-format-helper';
import {
  getDefaultCreateClassroomData,
  openCreateClassroomModal,
} from '../../helpers/management-classroom-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Services
import ManagementService from '../../services/management-service';

// Providers
import { GeneralContext } from '../../providers/general-provider';

const ManagementClassroom = () => {
  const management = new ManagementService();
  const navigate = useNavigate();
  const { classroomId } = useParams<{ classroomId: string }>();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const managementService = new ManagementService();

  const [
    defaultCreateClassroomData,
    setDefaultCreateClassroomData,
  ] = useState<CreateClassroomFormData>(null);
  const [createClassroomData, setCreateClassroomData] = useState(
    defaultCreateClassroomData
  );

  const titles: ListContentTitle[] = [
    { text: 'Nome' },
    { text: 'Gênero' },
    { text: 'Responsáveis' },
  ];

  const [error, setError] = useState(false);
  const [studentsList, setStudentsList] = useState<ListContent[]>(null);
  const [classroom, setClassroom] = useState<ClassroomStudents>(null);
  const updateClassroomData = () => {
    management
      .getClassroom(parseInt(classroomId))
      .then((classroom) => {
        setClassroom(classroom);
        setDefaultCreateClassroomData(getDefaultCreateClassroomData(classroom));
        setCreateClassroomData({
          id: classroom.id,
          name: classroom.name,
          period: classroom.period,
          diaryMode: classroom.diaryMode,
        });

        const newStudentsList = [];
        classroom.students.forEach((student) => {
          const eventItem: ListContent = {
            linkTo: `/gerenciar/aluno/${student.id}`,
            data: [
              {
                text: student.name,
              },
              {
                text: getGender(student.gender),
              },
              {
                text: student.responsibles
                  .map((responsible) => responsible.name)
                  .join('</br>'),
              },
            ],
          };
          newStudentsList.push(eventItem);
        });
        setStudentsList(newStudentsList);
      })
      .catch(() => setError(true));
  };
  useEffect(() => {
    updateClassroomData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateClassroomPromise = (data: CreateClassroomFormData) => {
    if (!data.name || !data.period || !data.diaryMode)
      return Promise.reject('Todos os campos devem ser preenchidos');

    return managementService
      .updateClassroom(data, data.id)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Turma editada!',
          type: 'success',
        });

        updateClassroomData();
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar a turma.',
          type: 'error',
        });
      });
  };

  const deleteClassroomPromise = () => {
    if (classroom.students.length > 0) {
      setModalContent({
        title: 'Erro ao deletar turma',
        modalContent: (
          <p>
            É necessário remover todos os alunos da turma <b>{classroom.name}</b> antes de deletá-la.
          </p>
        ),
        submitButtonTitle: null,
        submitButtonType: null,
        submitData: {},
        onSubmitButtonClick: null,
        isOpen: true,
      });
      return;
    }

    const deletePromise = ({ classroomId }: { classroomId: number }) => {
      return managementService
        .deleteClassroom(classroomId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Turma deletada!',
            type: 'success',
          });

          navigate('/gerenciar');
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar a turma.',
            type: 'error',
          });
        });
    };

    setModalContent({
      title: 'Deletar turma',
      modalContent: (
        <p>
          Deseja realmente deletar a turma <b>{classroom.name}</b>?
        </p>
      ),
      submitButtonTitle: 'confirmar deleção',
      submitButtonType: 'primary',
      submitData: { classroomId: parseInt(classroomId) },
      onSubmitButtonClick: deletePromise,
      isOpen: true,
    });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroom || !studentsList) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.singleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.top}
    >
      <ButtonsBar pattern='firstSeparate'>
        <FeatureTitle
          title={`${classroom.name} - ${getPeriodName(
            classroom.period
          )} - ${getDiaryMode(classroom.diaryMode)}`}
        />

        <SubmitButton
          value='editar turma'
          variant='secondary'
          onClick={() =>
            openCreateClassroomModal(
              createClassroomData,
              defaultCreateClassroomData,
              setCreateClassroomData,
              setModalContent,
              updateClassroomPromise,
              deleteClassroomPromise
            )
          }
        />
        <Link to={`/gerenciar/turma/${classroomId}/aluno/criar`}>
          <SubmitButton
            value='adicionar aluno'
            variant='primary'
            buttonStyle={{ marginLeft: '12px' }}
          />
        </Link>
      </ButtonsBar>

      {studentsList.length > 0 ? (
        <List titles={titles} contents={studentsList} />
      ) : (
        <Empty />
      )}
    </BaseContentContainer>
  );
};

export default ManagementClassroom;
